import { useCallback, useEffect, useMemo, useState } from 'react';
import { CaseOption } from '.';

type ParallelCaseHook = {
  parallelCasesOptions: CaseOption[];
  initialSelectedItem: CaseOption[] | null;
  onSelectItem: (item: CaseOption[] | null) => void;
  isDisabled?: boolean;
};

const useParallelCase = ({ parallelCasesOptions, initialSelectedItem, onSelectItem }: ParallelCaseHook) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<CaseOption[] | []>(initialSelectedItem || []);

  const isAllSelected = selectedItem.length === parallelCasesOptions.length;
  const updatedCases = useMemo(() => [...parallelCasesOptions], [parallelCasesOptions]);

  const handleItemClick = useCallback(
    (item: CaseOption) => {
      setSelectedItem((prevSelected) => {
        if (item.id === -1) {
          return isAllSelected ? [] : parallelCasesOptions;
        }

        if (prevSelected.some((i) => i.id === item.id)) {
          return prevSelected.filter((i) => i.id !== item.id);
        } else {
          return [...prevSelected, item];
        }
      });
    },
    [isAllSelected, parallelCasesOptions]
  );

  const handleCancel = useCallback(() => {
    setIsOpen(false);
    setSelectedItem(initialSelectedItem || []);
  }, [initialSelectedItem]);

  const handleApply = useCallback(() => {
    setIsOpen(false);
    onSelectItem(selectedItem);
  }, [onSelectItem, selectedItem]);

  useEffect(() => {
    setSelectedItem(initialSelectedItem || []);
  }, [initialSelectedItem]);

  return {
    setIsOpen,
    isOpen,
    selectedItem,
    setSelectedItem,
    handleItemClick,
    handleCancel,
    handleApply,
    updatedCases,
    isAllSelected,
  };
};

export default useParallelCase;
